





 
import { XMarkIcon } from "@heroicons/react/24/outline";
import TaskSubtasks from "./TaskSubtasks";
import TaskActivity2 from "./TaskActivity2";
import { updateTaskStatusApi } from "../../app/api";
import { useEffect, useState } from "react";
import TaskComments from "./TaskComments";
import LateCompletionModal from "./LateCompletionModal";
import HoldReasonModal from "./HoldReasonModal";
import { createPortal } from "react-dom";

import { CalendarRange } from "lucide-react";
 
 
const STATUS_LABELS = {
  TODO: "To Do",
  IN_PROGRESS: "In Progress",
  HOLD: "Hold",
  REVIEW: "Review",
  DONE: "Done",
};
 
const STATUS_FLOW = {
  TODO: ["IN_PROGRESS"],
  IN_PROGRESS: ["REVIEW", "HOLD"],
  REVIEW: ["DONE", "IN_PROGRESS"],
  HOLD: ["IN_PROGRESS"],
  DONE: [],
};
 
export default function TaskDrawer({
  open,
  task,
  onClose,
  onStatusChange,
}) {
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [showLateModal, setShowLateModal] = useState(false);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
 
  // 👇 NEW: track subtask progress
  const [subtaskProgress, setSubtaskProgress] = useState({
    total: 0,
    completed: 0,
  });
 
  useEffect(() => {
    setError(null);
    setSubtaskProgress({ total: 0, completed: 0 });
  }, [task?.id]);
 
  if (!open || !task) return null;
 
  const allowedStatuses = STATUS_FLOW[task.status] || [];
 
  const allSubtasksDone =
    subtaskProgress.total > 0 &&
    subtaskProgress.completed === subtaskProgress.total;
 
  // const isStatusLocked =
  //   subtaskProgress.total > 0 && !allSubtasksDone;
 
  const hasSubtasks = subtaskProgress.total > 0;
 
const isStatusLocked = hasSubtasks && !allSubtasksDone;
 
 
  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    if (newStatus === task.status) return;

    setUpdating(true);
    setError(null);

    try {
      // 🔥 If moving to HOLD, show hold modal first
      if (newStatus === "HOLD") {
        setPendingStatus(newStatus);
        setShowHoldModal(true);
        setUpdating(false);
        return;
      }

      // 🔥 If moving to DONE and it's late, show modal first
      if (newStatus === "DONE" && task.due_date) {
        const dueDate = new Date(task.due_date);
        dueDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (today > dueDate) {
          setPendingStatus(newStatus);
          setShowLateModal(true);
          setUpdating(false);
          return;
        }
      }

      await proceedStatusUpdate(newStatus);
    } catch (err) {
      setError(err.message || "Status change not allowed");
    } finally {
      setUpdating(false);
    }
  };

  const proceedStatusUpdate = async (newStatus, completionReason = null, holdReason = null) => {
    setUpdating(true);
    try {
      const res = await updateTaskStatusApi(task.id, {
        status: newStatus,
        completion_reason: completionReason,
        reason: holdReason
      });

      if (!res?.data?.id) {
        throw new Error("Invalid API response");
      }

      onStatusChange(res.data);
    } catch (err) {
      setError(err.message || "Status change not allowed");
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmLate = (reason) => {
    setShowLateModal(false);
    proceedStatusUpdate(pendingStatus, reason, null);
  };

  const handleConfirmHold = (reason) => {
    setShowHoldModal(false);
    proceedStatusUpdate(pendingStatus, null, reason);
  };
 
 
 
 
 
  return createPortal(
    <div className="fixed inset-0 z-50 flex">
      {/* OVERLAY */}
      <div
        className="flex-1 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
 
      {/* DRAWER */}
      <aside className="w-[420px] bg-white p-6 shadow-2xl flex flex-col overflow-y-auto">
 
 
 
<header className="flex justify-between items-start mb-4">
  <div className="flex items-center gap-2">
    <h2 className="text-lg font-semibold">
      {task.title}
    </h2>
 
    {/* 🔒 Blocked Badge */}
    {subtaskProgress.total > 0 && !allSubtasksDone && (
      <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
        {/* Blocked */}
        {STATUS_LABELS[task.status]}
      </span>
    )}
  </div>
 
  <button onClick={onClose}>
    <XMarkIcon className="w-5 h-5" />
  </button>
</header>
 
 
        {/* STATUS + META */}
        <div className="space-y-3 text-sm">
          <div>
            <label className="block mb-1 font-medium">
              Status
            </label>
 
<select
  value={task.status}
  onChange={handleStatusChange}
  disabled={
    updating ||
    allowedStatuses.length === 0 ||
    isStatusLocked
  }
  className="border rounded px-2 py-1 w-full disabled:bg-gray-100"
>
  {/* CURRENT STATUS */}
  <option value={task.status}>
    {STATUS_LABELS[task.status]}
  </option>
 
  {/* NEXT ALLOWED STATUSES */}
  {allowedStatuses.map((s) => (
    <option key={s} value={s}>
      {STATUS_LABELS[s]}
    </option>
  ))}
</select>
 
 
 
            {subtaskProgress.total > 0 && (
  <div className="mt-2">
    <div className="h-2 bg-gray-200 rounded-full">
      <div
        className="h-2 bg-blue-500 rounded-full transition-all"
        style={{
          width: `${
            (subtaskProgress.completed /
              subtaskProgress.total) *
            100
          }%`,
        }}
      />
    </div>
    <p className="text-xs text-gray-500 mt-1">
      {subtaskProgress.completed} of{" "}
      {subtaskProgress.total} subtasks completed
    </p>
  </div>
)}
 
 
            {/* 👇 NEW UX MESSAGE */}
            {isStatusLocked && (
              <div className="mt-1 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded px-2 py-1">
                Complete all subtasks to move this task forward
              </div>
            )}
          </div>
 
          <div>
            Priority:{" "}
            <span className="font-semibold text-red-600">
              {task.priority}
            </span>
          </div>









          {task.due_date && (
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-700">Due Date:</span>
              <div className="flex items-center gap-2">
                <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded text-xs font-bold border border-blue-100 flex items-center gap-1">
                  <CalendarRange className="w-3 h-3" />
                  {new Date(task.due_date).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric"
                  })}
                </span>
                
                {/* 🔥 Immediate Overdue Indicator */}
                {new Date(task.due_date) < new Date().setHours(0,0,0,0) && task.status !== 'DONE' && task.status !== 'HOLD' && (
                  <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold animate-pulse uppercase tracking-tight">
                    Overdue
                  </span>
                )}
              </div>
            </div>
          )}




 
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
              {error}
            </div>
          )}
        </div>
 
        <hr className="my-4" />
 
        {/* ===== SUBTASKS ===== */}
        <TaskSubtasks
          taskId={task.id}
          onProgressChange={setSubtaskProgress}
        />
 
        <hr className="my-4" />
 
 
{/* ===== COMMENTS ===== */}
<TaskComments taskId={task.id} />
 
<hr className="my-4" />
        {/* ===== ACTIVITY TIMELINE ===== */}
        {/* <TaskActivity taskId={task.id} /> */}
        {/* <div className="bg-[#EDE4F9] rounded-xl shadow-md p-4"> */}
  <TaskActivity2 taskId={task.id} />
{/* </div> */}
      </aside>

      <LateCompletionModal
        isOpen={showLateModal}
        onClose={() => setShowLateModal(false)}
        onConfirm={handleConfirmLate}
        taskTitle={task.title}
      />

      <HoldReasonModal
        isOpen={showHoldModal}
        onClose={() => setShowHoldModal(false)}
        onConfirm={handleConfirmHold}
        taskTitle={task.title}
      />
    </div>,
          document.body
  );
}